import type { ReactNode } from 'react';
import { LogoMark } from './LogoMark';

/**
 * Shared frame for the sign-in, reset and confirmation screens.
 *
 * v2 puts the title inside the card with the fields and leaves only a mono
 * wordmark above it, so the screen opens on the thing you came to do rather
 * than on a logo. The ground is the design's faint grid, masked to a soft
 * ellipse behind the card.
 */
export function AuthLayout({
  title,
  subtitle,
  footnote,
  children,
}: {
  title: string;
  subtitle: string;
  footnote: string;
  children: ReactNode;
}) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-8">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(color-mix(in oklch, var(--accent) 5%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in oklch, var(--accent) 5%, transparent) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'radial-gradient(55% 45% at 50% 40%, #000, transparent)',
          WebkitMaskImage: 'radial-gradient(55% 45% at 50% 40%, #000, transparent)',
        }}
      />
      <div className="relative flex w-full max-w-[348px] flex-col gap-[18px]">
        <div className="flex items-center gap-2.5">
          <LogoMark size={30} />
          <span
            className="text-[11.5px] tracking-normal"
            style={{ color: 'var(--muted)' }}
          >
            Sonasi&nbsp;/&nbsp;Console
          </span>
        </div>

        <div
          className="flex flex-col gap-3.5 rounded-[14px] border p-5"
          style={{ background: 'var(--panel)', borderColor: 'var(--line)' }}
        >
          <span className="flex flex-col gap-1.5">
            <h1 className="m-0 text-[17px] font-semibold tracking-[-0.02em]">{title}</h1>
            <span className="text-[12.5px] leading-[1.5]" style={{ color: 'var(--muted)' }}>
              {subtitle}
            </span>
          </span>
          {children}
          <span className="text-[11.5px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
            {footnote}
          </span>
        </div>
      </div>
    </div>
  );
}

/** A field's name, in the design's small uppercase mono. */
export function FieldLabel({ children }: { children: ReactNode }) {
  return (
    /*
      The same heading a card carries. The design sets "Work email" and
      "Password" in exactly the style it sets "Passkeys" and "Site copy" —
      13px, 600, `--text` — and the sign-in screen has two of them and nothing
      else, so they are the headings on it.
    */
    <span className="eyebrow">{children}</span>
  );
}
